import React, { useEffect, useState } from 'react';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { ARI_GLASS as GLASS, ARI_BOLT as BOLT, ARI_AVATAR as AVATAR } from '../lib/ariArt';

const LINES = [
  'Which charities actually fit our ESG goals?',
  'Match every gift my team makes, automatically.',
  'Show me where last quarter\u2019s giving went.',
  'Open payroll giving before the next cycle.',
];

const CHIPS = [
  { label: '1.8M+ verified charities', className: 'top-2 -left-2 lg:-left-8' },
  { label: '0% platform fee', className: 'top-1/3 -right-2 lg:-right-6' },
  { label: 'Every dollar traced to payout', className: 'bottom-6 -left-3 lg:-left-10' },
];

export const AriEmblem = () => {
  const [lineIndex, setLineIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [thinking, setThinking] = useState(false);
  const [surge, setSurge] = useState(false);

  useEffect(() => {
    const onTyping = (e) => setThinking(Boolean(e.detail?.typing));
    window.addEventListener('altrunic:ari-typing', onTyping);
    return () => window.removeEventListener('altrunic:ari-typing', onTyping);
  }, []);

  // the heart surges once whenever a real donation completes
  useEffect(() => {
    let seen = null;
    let stopped = false;
    let surgeTimer;

    const poll = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/live-giving`);
        if (!res.ok) return;
        const { completed_count: count } = await res.json();
        if (stopped || typeof count !== 'number') return;
        if (seen !== null && count > seen) {
          setSurge(true);
          surgeTimer = setTimeout(() => setSurge(false), 1400);
        }
        seen = count;
      } catch {
        /* marketing page keeps beating regardless */
      }
    };

    poll();
    const id = setInterval(poll, 20000);
    return () => {
      stopped = true;
      clearInterval(id);
      clearTimeout(surgeTimer);
    };
  }, []);

  useEffect(() => {
    const full = LINES[lineIndex];
    if (typed.length < full.length) {
      const t = setTimeout(() => setTyped(full.slice(0, typed.length + 1)), 38);
      return () => clearTimeout(t);
    }
    const hold = setTimeout(() => {
      setTyped('');
      setLineIndex((i) => (i + 1) % LINES.length);
    }, 2600);
    return () => clearTimeout(hold);
  }, [typed, lineIndex]);

  const openChat = () => window.dispatchEvent(new CustomEvent('altrunic:open-chat'));

  return (
    <div
      className={`w-full max-w-[440px] mx-auto ${thinking ? 'alt-thinking' : ''} ${surge ? 'alt-surging' : ''}`}
      data-testid="ari-emblem"
      data-thinking={thinking ? 'true' : 'false'}
      data-surging={surge ? 'true' : 'false'}
    >
      {/* Emblem */}
      <div className="relative aspect-square">
        <div className="absolute inset-[12%] rounded-full bg-blue-600/25 blur-[70px] alt-orb-core" />

        {/* a ring breaks outward on the surge, so a landed gift reads at a glance */}
        {surge && (
          <div
            className="absolute inset-[7%] rounded-full border-2 border-sky-300/70 alt-surge-ring pointer-events-none"
            data-testid="ari-surge-ring"
          />
        )}

        {/* rotating technical rings */}
        <div className="absolute inset-0 rounded-full alt-ring-dashed alt-spin" />
        <div className="absolute inset-[9%] rounded-full alt-ring-arc alt-spin-rev" />
        <div className="absolute inset-[18%] rounded-full border border-blue-400/10" />

        {/* orbiting node */}
        <div className="absolute inset-[4%] alt-spin-fast">
          <span className="absolute left-1/2 -top-1 -translate-x-1/2 w-2 h-2 rounded-full bg-sky-300 shadow-[0_0_14px_4px_rgba(56,189,248,0.7)]" />
        </div>

        {/* the glass holds still */}
        <img
          src={GLASS}
          alt="Ari, the Altrunic impact guide"
          className="absolute inset-[7%] w-[86%] h-[86%] object-contain alt-screen alt-emblem-mask select-none"
          draggable="false"
        />

        {/* the electric heart beats and crackles inside the glass - flares are
            positioned well inside the sphere so none of it escapes */}
        <div className="absolute inset-[15%]" data-testid="ari-heart-chamber">
          <img
            src={BOLT}
            alt=""
            aria-hidden="true"
            className={`absolute inset-[12%] w-[76%] h-[76%] object-contain alt-screen alt-heart-mask alt-heartbeat select-none ${surge ? 'alt-surge' : ''}`}
            draggable="false"
          />
        </div>

        {CHIPS.map((chip) => (
          <div
            key={chip.label}
            className={`absolute ${chip.className} alt-float-slow px-3.5 py-2 rounded-xl bg-slate-900/85 backdrop-blur-xl border border-slate-700/60 shadow-[0_8px_28px_rgba(0,0,0,0.55)]`}
          >
            <span className="text-[11px] font-semibold text-slate-200 whitespace-nowrap">{chip.label}</span>
          </div>
        ))}
      </div>

      {/* Ari card */}
      <div className="relative -mt-4 rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 p-5 shadow-[0_18px_50px_rgba(0,0,0,0.55)]" data-testid="ari-intro-card">
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-blue-500/30 via-indigo-500/10 to-transparent -z-10 blur-[2px]" />
        <div className="flex items-center gap-3">
          <img src={AVATAR} alt="Ari" className="w-11 h-11 rounded-full object-cover alt-screen alt-orb-glass ring-1 ring-blue-500/30" />
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-slate-50 leading-tight">Hi, I&rsquo;m Ari</p>
            <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-blue-400 flex items-center gap-1.5" data-testid="ari-status">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              {surge ? 'A gift just landed' : thinking ? 'Thinking\u2026' : 'Altrunic\u2019s impact guide'}
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-xl bg-slate-950/70 border border-slate-800/80 px-3.5 py-3 min-h-[62px]">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            {thinking ? 'Working on it' : 'People ask me'}
          </p>
          {thinking ? (
            <p className="mt-1 text-sm text-blue-200 leading-snug flex items-center gap-1" data-testid="ari-thinking-line">
              Pulling that together
              <span className="alt-dot">.</span>
              <span className="alt-dot alt-dot-2">.</span>
              <span className="alt-dot alt-dot-3">.</span>
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-200 leading-snug alt-caret" data-testid="ari-typed-line">{typed}</p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={openChat}
            data-testid="ari-ask-btn"
            className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-colors duration-200"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Ask Ari anything
          </button>
          <a
            href="#meet-ari"
            data-testid="ari-see-more-btn"
            className="group flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white text-xs font-bold transition-colors duration-200"
          >
            See what I can do
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AriEmblem;
